import React, { useState, useEffect, useCallback, useRef } from 'react';
import { primaryButtonSx, pagePaperSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Card,
  CardContent,
  Grid,
  Pagination,
  Tooltip,
  Avatar,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Support as SupportIcon,
  BugReport as BugReportIcon,
  Lightbulb as LightbulbIcon,
  Help as HelpIcon,
  Build as BuildIcon,
  Payment as PaymentIcon,
  MoreHoriz as MoreIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  ChatBubble as ChatBubbleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface SupportTicket {
  ticket_id: string;
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  ticket_type: 'bug' | 'feature_request' | 'general_inquiry' | 'technical_support' | 'billing' | 'other';
  status: 'open' | 'in_progress' | 'waiting_for_response' | 'resolved' | 'closed';
  user_name: string;
  user_email: string;
  created_at: string;
  updated_at: string;
  response_count: number;
  last_response_at?: string;
}

interface TicketResponse {
  response_id: string;
  responder_email: string;
  responder_name: string;
  responder_type: 'user' | 'support' | 'system';
  message: string;
  is_internal: boolean;
  created_at: string;
}

interface Attachment {
  attachment_id: string;
  filename: string;
  file_url: string;
  file_size: number;
  mime_type: string;
  uploaded_by: string;
  created_at: string;
  response_id?: string;
}

interface TicketDetails extends SupportTicket {
  responses: TicketResponse[];
  attachments: Attachment[];
  business_name: string;
}

interface TicketStats {
  total_tickets: number;
  open_tickets: number;
  in_progress_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  critical_tickets: number;
  high_priority_tickets: number;
  avg_resolution_time_hours: number | string | null;
}

const priorityColors: Record<string, string> = {
  low: '#22c55e',
  medium: '#f59e0b',
  high: '#f97316',
  critical: '#ef4444',
};

const statusColors: Record<string, string> = {
  open: '#6b7280',
  in_progress: '#f59e0b',
  waiting_for_response: '#8b5cf6',
  resolved: '#22c55e',
  closed: '#64748b',
};

const ticketTypeLabels: Record<string, string> = {
  bug: 'Bug Report',
  feature_request: 'Feature Request',
  general_inquiry: 'General Inquiry',
  technical_support: 'Technical Support',
  billing: 'Billing',
  other: 'Other',
};

const ticketTypeIcons: Record<string, React.ReactNode> = {
  bug: <BugReportIcon fontSize="small" />,
  feature_request: <LightbulbIcon fontSize="small" />,
  general_inquiry: <HelpIcon fontSize="small" />,
  technical_support: <BuildIcon fontSize="small" />,
  billing: <PaymentIcon fontSize="small" />,
  other: <MoreIcon fontSize="small" />,
};

function formatDate(dateString: string) {
  const d = new Date(dateString);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(mimeType: string) {
  return mimeType.startsWith('image/');
}

// ─── Attachment thumbnail/link ────────────────────────────────────────────────
const AttachmentItem: React.FC<{ attachment: Attachment }> = ({ attachment }) => {
  if (isImage(attachment.mime_type)) {
    return (
      <Box
        component="a"
        href={attachment.file_url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: 'inline-block', mr: 1, mb: 1 }}
      >
        <Box
          component="img"
          src={attachment.file_url}
          alt={attachment.filename}
          sx={{
            maxWidth: 200,
            maxHeight: 150,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            objectFit: 'cover',
            cursor: 'pointer',
            '&:hover': { opacity: 0.85 },
          }}
        />
      </Box>
    );
  }
  return (
    <Chip
      icon={<FileIcon />}
      label={`${attachment.filename} (${formatFileSize(attachment.file_size)})`}
      component="a"
      href={attachment.file_url}
      target="_blank"
      rel="noopener noreferrer"
      clickable
      variant="outlined"
      size="small"
      sx={{ mr: 1, mb: 1 }}
    />
  );
};

// ─── Chat message bubble ──────────────────────────────────────────────────────
const MessageBubble: React.FC<{ response: TicketResponse; attachments: Attachment[] }> = ({
  response,
  attachments,
}) => {
  const isSupport = response.responder_type === 'support';
  const msgAttachments = attachments.filter(a => a.response_id === response.response_id);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSupport ? 'row' : 'row-reverse',
        alignItems: 'flex-start',
        gap: 1.5,
        mb: 2,
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: isSupport ? 'primary.main' : 'grey.400',
          fontSize: '0.85rem',
          flexShrink: 0,
        }}
      >
        {isSupport ? 'S' : response.responder_name.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ maxWidth: '75%' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 0.5, display: 'block', textAlign: isSupport ? 'left' : 'right' }}
        >
          {response.responder_name} &middot; {formatDate(response.created_at)}
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: isSupport ? 'primary.50' : 'grey.100',
            border: '1px solid',
            borderColor: isSupport ? 'primary.200' : 'grey.200',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {response.message}
          </Typography>
          {msgAttachments.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {msgAttachments.map(a => (
                <AttachmentItem key={a.attachment_id} attachment={a} />
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const SupportTickets: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [replying, setReplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetails | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ status: 'all', priority: 'all' });
  const [replyMessage, setReplyMessage] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [createFiles, setCreateFiles] = useState<File[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createFileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    ticket_type: 'general_inquiry' as SupportTicket['ticket_type'],
  });

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        status: filters.status,
        priority: filters.priority,
      });
      const res = await axios.get(`${API_BASE_URL}/support-tickets?${params}`, authHeader());
      if (res.data.success) {
        setTickets(res.data.tickets);
        setTotalPages(res.data.pagination.pages);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/support-tickets/stats`, authHeader());
      if (res.data.success) setStats(res.data.stats);
    } catch {}
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [currentPage, filters, fetchTickets, fetchStats]);

  const openTicketDetail = async (ticketId: string) => {
    try {
      setLoadingDetail(true);
      setDetailDialogOpen(true);
      const res = await axios.get(`${API_BASE_URL}/support-tickets/${ticketId}`, authHeader());
      if (res.data.success) {
        setSelectedTicket(res.data.ticket);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load ticket details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReply = async () => {
    if (!selectedTicket || !replyMessage.trim()) return;
    try {
      setReplying(true);
      const res = await axios.post(
        `${API_BASE_URL}/support-tickets/${selectedTicket.ticket_id}/reply`,
        { message: replyMessage.trim() },
        authHeader()
      );

      if (res.data.success && pendingFiles.length > 0) {
        setUploadProgress(true);
        for (const file of pendingFiles) {
          const fd = new FormData();
          fd.append('file', file);
          if (res.data.responseId) fd.append('response_id', res.data.responseId);
          await axios.post(
            `${API_BASE_URL}/support-tickets/${selectedTicket.ticket_id}/attachments`,
            fd,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
        }
        setUploadProgress(false);
      }

      setReplyMessage('');
      setPendingFiles([]);
      // Refresh detail
      await openTicketDetail(selectedTicket.ticket_id);
      fetchTickets();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setReplying(false);
      setUploadProgress(false);
    }
  };

  const handleCreateSubmit = async () => {
    if (!formData.subject.trim() || !formData.description.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const res = await axios.post(`${API_BASE_URL}/support-tickets`, formData, authHeader());

      // Upload any attachments added during ticket creation
      if (res.data.success && createFiles.length > 0) {
        setUploadProgress(true);
        for (const file of createFiles) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(
            `${API_BASE_URL}/support-tickets/${res.data.ticketId}/attachments`,
            fd,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
        }
        setUploadProgress(false);
      }

      setSuccess('Support ticket submitted. A confirmation has been sent to your email.');
      setCreateDialogOpen(false);
      setFormData({ subject: '', description: '', priority: 'medium', ticket_type: 'general_inquiry' });
      setCreateFiles([]);
      fetchTickets();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create support ticket');
    } finally {
      setSubmitting(false);
      setUploadProgress(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPendingFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader
        title="Support"
        subtitle="Admin"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={primaryButtonSx}
          >
            Raise a Ticket
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total', value: stats.total_tickets, accent: 'text.disabled' },
            { label: 'Open', value: stats.open_tickets, accent: 'warning.main' },
            { label: 'In Progress', value: stats.in_progress_tickets, accent: 'info.main' },
            { label: 'Resolved', value: stats.resolved_tickets, accent: 'success.main' },
            { label: 'Critical', value: stats.critical_tickets, accent: 'error.main' },
          ].map(s => (
            <Grid item xs={6} sm={4} md key={s.label}>
              <Card sx={{ ...pagePaperSx, borderLeft: '3px solid', borderLeftColor: s.accent }}>
                <CardContent sx={{ pb: '12px !important', pt: 2 }}>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>{s.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {Number(stats.avg_resolution_time_hours) > 0 && (
            <Grid item xs={6} sm={4} md>
              <Card sx={{ ...pagePaperSx, borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
                <CardContent sx={{ pb: '12px !important', pt: 2 }}>
                  <Typography variant="caption" color="text.secondary">Avg Resolution</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {Number(stats.avg_resolution_time_hours).toFixed(1)}h
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ ...pagePaperSx, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label="Status" onChange={e => { setFilters(p => ({ ...p, status: e.target.value })); setCurrentPage(1); }}>
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="waiting_for_response">Waiting for Response</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={filters.priority} label="Priority" onChange={e => { setFilters(p => ({ ...p, priority: e.target.value })); setCurrentPage(1); }}>
              <MenuItem value="all">All Priority</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={() => { fetchTickets(); fetchStats(); }} disabled={loading} sx={primaryButtonSx}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper sx={pagePaperSx}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Subject</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Responses</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                </TableRow>
              ) : tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <SupportIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No tickets found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map(ticket => (
                  <TableRow key={ticket.ticket_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{ticket.subject}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        #{ticket.ticket_id.substring(0, 8)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {ticketTypeIcons[ticket.ticket_type]}
                        <Typography variant="body2">{ticketTypeLabels[ticket.ticket_type]}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.priority.toUpperCase()}
                        size="small"
                        sx={{ bgcolor: priorityColors[ticket.priority], color: 'white', fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.status.replace(/_/g, ' ').toUpperCase()}
                        size="small"
                        sx={{ bgcolor: statusColors[ticket.status], color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(ticket.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ChatBubbleIcon fontSize="small" sx={{ color: ticket.response_count > 0 ? 'primary.main' : 'text.disabled' }} />
                        <Typography variant="body2">{ticket.response_count}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View & Reply">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openTicketDetail(ticket.ticket_id)}
                          startIcon={<OpenInNewIcon />}
                        >
                          View
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination count={totalPages} page={currentPage} onChange={(_, p) => setCurrentPage(p)} color="primary" />
          </Box>
        )}
      </Paper>

      {/* ── Ticket Detail Dialog ───────────────────────────────────────────── */}
      <Dialog open={detailDialogOpen} onClose={() => { setDetailDialogOpen(false); setSelectedTicket(null); setReplyMessage(''); setPendingFiles([]); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupportIcon color="primary" />
            <Typography variant="h6">
              {selectedTicket ? selectedTicket.subject : 'Loading…'}
            </Typography>
          </Box>
          <IconButton onClick={() => { setDetailDialogOpen(false); setSelectedTicket(null); setReplyMessage(''); setPendingFiles([]); }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {loadingDetail || !selectedTicket ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '65vh' }}>
              {/* Ticket meta bar */}
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  <Chip
                    label={selectedTicket.priority.toUpperCase()}
                    size="small"
                    sx={{ bgcolor: priorityColors[selectedTicket.priority], color: 'white', fontWeight: 700 }}
                  />
                  <Chip
                    label={selectedTicket.status.replace(/_/g, ' ').toUpperCase()}
                    size="small"
                    sx={{ bgcolor: statusColors[selectedTicket.status], color: 'white' }}
                  />
                  <Chip label={ticketTypeLabels[selectedTicket.ticket_type]} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Opened {formatDate(selectedTicket.created_at)}
                  </Typography>
                </Stack>

                {/* Original description */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Original request
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {selectedTicket.description}
                  </Typography>
                  {/* Attachments on ticket (no response_id) */}
                  {selectedTicket.attachments.filter(a => !a.response_id).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {selectedTicket.attachments.filter(a => !a.response_id).map(a => (
                        <AttachmentItem key={a.attachment_id} attachment={a} />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Message thread */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {selectedTicket.responses.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ChatBubbleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No replies yet. Our team will respond soon.</Typography>
                  </Box>
                ) : (
                  selectedTicket.responses.map(r => (
                    <MessageBubble key={r.response_id} response={r} attachments={selectedTicket.attachments} />
                  ))
                )}
                <div ref={chatEndRef} />
              </Box>

              {/* Reply input */}
              {!['resolved', 'closed'].includes(selectedTicket.status) && (
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  {pendingFiles.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                      {pendingFiles.map((f, i) => (
                        <Chip
                          key={i}
                          label={f.name}
                          icon={isImage(f.type) ? <ImageIcon /> : <FileIcon />}
                          onDelete={() => setPendingFiles(p => p.filter((_, idx) => idx !== i))}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  )}
                  {uploadProgress && <LinearProgress sx={{ mb: 1 }} />}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                      placeholder="Type your reply…"
                      value={replyMessage}
                      onChange={e => setReplyMessage(e.target.value)}
                      multiline
                      maxRows={4}
                      fullWidth
                      size="small"
                      onKeyDown={e => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleReply();
                      }}
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />
                    <Tooltip title="Attach file">
                      <IconButton onClick={() => fileInputRef.current?.click()}>
                        <AttachFileIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Send (Ctrl+Enter)">
                      <span>
                        <IconButton
                          color="primary"
                          onClick={handleReply}
                          disabled={replying || (!replyMessage.trim() && pendingFiles.length === 0)}
                        >
                          {replying ? <CircularProgress size={20} /> : <SendIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Typography variant="caption" color="text.secondary">Ctrl+Enter to send</Typography>
                </Box>
              )}
              {['resolved', 'closed'].includes(selectedTicket.status) && (
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    This ticket is {selectedTicket.status}. Contact support to reopen.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Create Ticket Dialog ───────────────────────────────────────────── */}
      <Dialog
        open={createDialogOpen}
        onClose={() => { setCreateDialogOpen(false); setCreateFiles([]); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupportIcon color="primary" />
            <Typography variant="h6">New Support Ticket</Typography>
          </Box>
          <IconButton onClick={() => { setCreateDialogOpen(false); setCreateFiles([]); }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              label="Subject"
              value={formData.subject}
              onChange={e => setFormData(p => ({ ...p, subject: e.target.value }))}
              fullWidth
              required
              placeholder="Brief summary of your issue or request"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Ticket Type</InputLabel>
                <Select value={formData.ticket_type} label="Ticket Type" onChange={e => setFormData(p => ({ ...p, ticket_type: e.target.value as any }))}>
                  <MenuItem value="bug">Bug Report</MenuItem>
                  <MenuItem value="feature_request">Feature Request</MenuItem>
                  <MenuItem value="general_inquiry">General Inquiry</MenuItem>
                  <MenuItem value="technical_support">Technical Support</MenuItem>
                  <MenuItem value="billing">Billing</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={formData.priority} label="Priority" onChange={e => setFormData(p => ({ ...p, priority: e.target.value as any }))}>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Description"
              value={formData.description}
              onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              fullWidth
              multiline
              rows={6}
              required
              placeholder="Please provide as much detail as possible. For bugs, include the steps to reproduce the issue."
            />

            {/* Attachments */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Attachments (optional)</Typography>
              <input
                ref={createFileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt"
                style={{ display: 'none' }}
                onChange={e => {
                  if (e.target.files) setCreateFiles(p => [...p, ...Array.from(e.target.files!)]);
                }}
              />
              {createFiles.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                  {createFiles.map((f, i) => (
                    <Chip
                      key={i}
                      label={f.name}
                      icon={isImage(f.type) ? <ImageIcon /> : <FileIcon />}
                      onDelete={() => setCreateFiles(p => p.filter((_, idx) => idx !== i))}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
              {uploadProgress && <LinearProgress sx={{ mb: 1 }} />}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                onClick={() => createFileInputRef.current?.click()}
              >
                Attach Files or Images
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Accepted: images, PDF, Word documents, plain text. Max 10 MB per file.
              </Typography>
            </Box>

            <Alert severity="info" icon={false} sx={{ borderRadius: 1 }}>
              A confirmation email will be sent to you when your ticket is submitted. You will also receive notifications for any updates.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setCreateDialogOpen(false); setCreateFiles([]); }}>Cancel</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} /> : <SendIcon />}
            sx={primaryButtonSx}
          >
            {submitting ? 'Submitting…' : 'Submit Ticket'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportTickets;
